import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import * as z from 'zod';
import { useUpdateSharepointInfo } from '@/app/hooks/queries/useSharepoint';
import { Button } from '@/app/ui/components/button';
import { Spinner } from '@/app/ui/components/spinner';
import {
  SHAREPOINT_CODE_VERIFIER_SESSION_KEY,
  SHAREPOINT_POST_LOGIN_RETURN_URL,
  SHAREPOINT_POST_LOGOUT_RETURN_URL,
  SHAREPOINT_STATE_PARAM,
} from '@/app/utils/constants';
import { validator } from '@/app/validator';

export const Route = createFileRoute('/(auth)/_layout/auth/callback')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      code: z.string().optional(),
      error: z.string().optional(),
      state: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations('general');
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);

  const { mutateAsync: updateSharepointInfo } = useUpdateSharepointInfo();

  const clearReturnUrls = () => {
    sessionStorage.removeItem(SHAREPOINT_POST_LOGOUT_RETURN_URL);
    sessionStorage.removeItem(SHAREPOINT_POST_LOGIN_RETURN_URL);
  };

  const isStateParamValid = (returnedState: string | undefined) => {
    if (!returnedState) return false;
    const storedState = sessionStorage.getItem(SHAREPOINT_STATE_PARAM);

    return returnedState === storedState;
  };

  const isReturnToValid = (returnTo: string) => {
    return returnTo.startsWith(window.location.origin);
  };

  const getReturnToUrl = () => {
    const returnTo =
      sessionStorage.getItem(SHAREPOINT_POST_LOGOUT_RETURN_URL) ??
      sessionStorage.getItem(SHAREPOINT_POST_LOGIN_RETURN_URL);

    if (returnTo && isReturnToValid(returnTo)) {
      return returnTo;
    } else {
      return '/';
    }
  };

  const handleRedirectOnError = () => {
    const returnTo = getReturnToUrl();
    clearReturnUrls();
    navigate({ to: returnTo, replace: true });
  };

  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const code = searchParams.code;
    const err = searchParams.error;
    const state = searchParams.state;
    const codeVerifier = sessionStorage.getItem(SHAREPOINT_CODE_VERIFIER_SESSION_KEY) || '';
    const postLogoutReturnTo = sessionStorage.getItem(SHAREPOINT_POST_LOGOUT_RETURN_URL);

    // 1. Explicit error from provider
    if (err) {
      setError(err);
      return;
    }

    if (code && !codeVerifier) {
      setError(t('somethingWentWrong'));
      return;
    }

    // 3. Nothing meaningful in query or storage -> error state
    if (!code && !postLogoutReturnTo && !codeVerifier) {
      setError(t('somethingWentWrong'));
      return;
    }

    // 4. Login completion path
    if (code && codeVerifier) {
      if (!isStateParamValid(state)) {
        setError(t('somethingWentWrong'));
      } else {
        updateSharepointInfo({ code, codeVerifier })
          .then(() => {
            sessionStorage.removeItem(SHAREPOINT_CODE_VERIFIER_SESSION_KEY);
            sessionStorage.removeItem(SHAREPOINT_STATE_PARAM);

            const returnToItem = sessionStorage.getItem(SHAREPOINT_POST_LOGIN_RETURN_URL);
            const returnToUrl = returnToItem && isReturnToValid(returnToItem) ? returnToItem : '/';

            clearReturnUrls();
            navigate({ to: returnToUrl, replace: true });
          })
          .catch(() => {
            setError(t('somethingWentWrong'));
          });
      }
      return;
    }

    // 5. Logout redirect path (validate URL)
    if (postLogoutReturnTo) {
      clearReturnUrls();
      if (isReturnToValid(postLogoutReturnTo)) {
        navigate({ to: postLogoutReturnTo, replace: true });
      } else {
        navigate({ to: '/', replace: true });
      }
      return;
    }

    // 6. Fallback (should rarely reach due to earlier returns)
    navigate({ to: '/', replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      {error && (
        <div className="flex flex-col items-center gap-5">
          <div className="rounded-xl bg-red-100 p-4 text-red-900 shadow">{error}</div>
          <Button onClick={handleRedirectOnError}>{t('returnToVolve')}</Button>
        </div>
      )}

      {!error && <Spinner size="lg" kind="inset" />}
    </div>
  );
}
